import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

import useObGoalsApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';

const Container = styled.div``;

const Modal = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.5);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center; justify-content: center; z-index: 1300; padding: 24px;
`;
const ModalCard = styled.div`
  background: #fff; border-radius: 8px; max-width: 480px; width: 100%; padding: 24px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
`;
const Field = styled.div` display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; font-size: 14px; `;
const FieldLabel = styled.label` color: #64748b; font-weight: 500; `;
const FieldInput = styled.input` padding: 8px 12px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 14px; `;
const FieldSelect = styled.select` padding: 8px 12px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 14px; `;
const FieldText = styled.textarea` padding: 8px 12px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 14px; resize: vertical; min-height: 60px; font-family: inherit; `;
const Actions = styled.div` display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px; `;
// #72 — yerel Primary/Secondary (kopyalanmis blok — onboarding-*/ats-*
// sayfalarinda ayni tarif) kaldirildi, paylasilan Button'a baglandi
// (bkz. t6-report.md).

export default function ObGoalsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite('OB') : false;

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const [scoreDialog, setScoreDialog] = useState({ isOpen: false, id: null, title: '', score: '3', finalStatus: 'achieved', reflection: '' });
  const [busy, setBusy] = useState(false);

  // drawer: { mode: 'create' } | { mode: 'edit', initialValues }
  const [drawer, setDrawer] = useState(null);

  const {
    fetching, data, pagination, columns: baseColumns,
    objectSchema, toFormValues, create, update,
    start, cancel, agreeManager, score,
  } = useObGoalsApi(queryParams);

  const handleDataStateChange = useCallback((act, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const handleNewButtonClick = useCallback(() => setDrawer({ mode: 'create' }), []);

  const handleSave = useCallback(async (formData) => {
    const success = drawer?.mode === 'edit'
      ? await update(drawer.initialValues.id, formData)
      : await create(formData);
    if (success) setDrawer(null);
    return success;
  }, [drawer, create, update]);

  const buildActions = useCallback((row) => {
    if (!row || !writable) return [];
    const actions = [];
    if (row.status === 'set' || row.status === 'in_progress') {
      actions.push({ type: 'edit', label: t('common.edit'),
        onClick: () => setDrawer({ mode: 'edit', initialValues: toFormValues(row) }) });
    }
    if (row.status === 'set') {
      // OBB-08 — "Çalışan Onayı Ver" KALDIRILDI. Hedefin ÇALIŞAN tarafındaki
      // mutabakatını İK/yönetici çalışan adına kaydedebiliyordu; imza artık
      // yalnız çalışanın kendi kimlik ailesinden kabul ediliyor (backend 403).
      // Çalışanın kendi yüzeyi hem webde (İşe Alıştırmam → Hedefler) hem
      // mobilde (Hedeflerim) çalışıyor.
      if (!row.agreedByManagerAt) actions.push({ type: 'approve', label: t('onboardingGoal.action.agreeManager'),
        onClick: async () => { await agreeManager(row.id); } });
      actions.push({ type: 'resume', label: t('onboardingGoal.action.start'),
        onClick: async () => { await start(row.id); } });
    }
    if (row.status === 'in_progress' || row.status === 'set') {
      actions.push({ type: 'scorecard', label: t('onboardingGoal.action.score'),
        onClick: () => setScoreDialog({
          isOpen: true, id: row.id, title: row.title,
          score: String(row.managerScore ?? 3),
          finalStatus: 'achieved', reflection: row.employeeReflection ?? '',
          notFullyAgreed: !row.agreedByEmployeeAt || !row.agreedByManagerAt,
        })
      });
    }
    if (row.status === 'set' || row.status === 'in_progress') {
      actions.push({ type: 'divider' });
      actions.push({ type: 'cancel', label: t('onboardingGoal.action.cancel'),
        onClick: async () => { await cancel(row.id); } });
    }
    return actions;
  }, [t, writable, agreeManager, start, cancel, toFormValues]);

  const columns = useMemo(() => {
    if (!writable) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const actions = buildActions(row);
            return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
          },
        },
      },
    ];
  }, [baseColumns, data, buildActions, writable]);

  return (
    <Container>
      <GenericFormDrawer
        open={!!drawer}
        onClose={() => setDrawer(null)}
        title={drawer?.mode === 'edit' ? t('onboardingGoal.edit') : t('onboardingGoal.new')}
        schema={objectSchema}
        initialValues={drawer?.mode === 'edit' ? drawer.initialValues : null}
        onSave={handleSave}
      />

      <Modal $open={scoreDialog.isOpen}>
        {scoreDialog.isOpen && (
          <ModalCard>
            <h3 style={{ margin: '0 0 16px' }}>{t('onboardingGoal.action.score')} — {scoreDialog.title}</h3>
            {scoreDialog.notFullyAgreed && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 8,
                background: 'rgba(255, 193, 7, 0.12)', border: '1px solid rgba(255, 193, 7, 0.45)',
                borderRadius: 8, padding: '10px 12px', marginBottom: 16,
                fontSize: 13, color: '#8A6D00', lineHeight: 1.45,
              }}>
                <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>{t('onboardingGoal.scoreWithoutApprovalWarn')}</span>
              </div>
            )}
            <Field>
              <FieldLabel>{t('onboardingGoal.managerScore')} (1-5)</FieldLabel>
              <FieldInput type="number" min="1" max="5" value={scoreDialog.score}
                onChange={(e) => setScoreDialog({ ...scoreDialog, score: e.target.value })} />
            </Field>
            <Field>
              <FieldLabel>{t('onboardingGoal.finalStatus')}</FieldLabel>
              <FieldSelect value={scoreDialog.finalStatus}
                onChange={(e) => setScoreDialog({ ...scoreDialog, finalStatus: e.target.value })}>
                <option value="achieved">{t('onboardingGoal.status.achieved')}</option>
                <option value="partially_achieved">{t('onboardingGoal.status.partially_achieved')}</option>
                <option value="missed">{t('onboardingGoal.status.missed')}</option>
              </FieldSelect>
            </Field>
            <Field>
              <FieldLabel>{t('onboardingGoal.employeeReflection')}</FieldLabel>
              <FieldText value={scoreDialog.reflection}
                onChange={(e) => setScoreDialog({ ...scoreDialog, reflection: e.target.value })} />
            </Field>
            <Actions>
              <Button variant="outline" onClick={() => setScoreDialog({ isOpen: false, id: null, title: '', score: '3', finalStatus: 'achieved', reflection: '' })}>{t('common.cancel')}</Button>
              <Button disabled={busy} onClick={async () => {
                setBusy(true);
                await score(scoreDialog.id, {
                  managerScore: parseInt(scoreDialog.score, 10),
                  finalStatus: scoreDialog.finalStatus,
                  employeeReflection: scoreDialog.reflection || undefined,
                });
                setScoreDialog({ isOpen: false, id: null, title: '', score: '3', finalStatus: 'achieved', reflection: '' });
                setBusy(false);
              }}>{busy ? '...' : t('common.confirm')}</Button>
            </Actions>
          </ModalCard>
        )}
      </Modal>

      {useMemo(() => (
        <DataTable
          title={t('onboardingGoal.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={writable}
          onNewButtonClick={handleNewButtonClick}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, writable, handleNewButtonClick,
        filterList, handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
